import {
    ChangeDetectionStrategy,
    Component,
    Input,
    inject,
    ViewChild,
    ViewEncapsulation,
    input,
    output,
    OnInit,
    Signal,
} from '@angular/core';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { ILightWeightObject } from '@fi-shared/models';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { UserService } from '@fi-app/user/user.service';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';
import { ContactService } from '@fi-shared/fi-contacts/contact.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatSort, Sort } from '@angular/material/sort';
import { Observable } from 'rxjs';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { IContactModel } from '@fi-shared/models/common';
import { getSystemObjectColorClass } from '@fi-shared/utils/getSystemObjectColorClass';
import { FiUserSharedService } from '@fi-shared/fi-user-shared/fi-user-shared.service';
import { IUserListItemViewModel } from '@fi-app/user/user';

@Component({
    selector: 'fi-user-list-table',
    templateUrl: './fi-user-list-table.component.html',
    styleUrls: ['./fi-user-list-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-list-table' },
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', border: 'none' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false,
})
export class FiUserListTableComponent implements OnInit {
    public items = input<IUserListItemViewModel[]>([]);
    public selectedItems = input<IUserListItemViewModel[]>([]);
    public loading = input<boolean>(false);

    @Input() isPhoneContactColumnShown: boolean;
    @Input() isHeaderRowShown: boolean = true;
    @Input() sortColumn: string;
    @Input() matSortDirection: 'desc' | 'asc';
    @Input() onDataChanged$: Observable<void>;
    @Input() hasCheckedColumn: boolean;
    @Input() isNestedList: boolean;
    @Input() isRemarkColumnShown: boolean;
    @Input() isEmailColumnShown: boolean;
    @Input() allowMultiSelect = true;
    @Input() textMode = false;
    @Input() totalCount: number;

    public checkedItemsChange = output<IUserListItemViewModel[]>();
    public matSortChange = output<Sort>();
    public scrollToBottom = output<void>();

    @ViewChild(MatSort, { static: false }) matSort: MatSort;

    private stateService = inject(FiStateService);
    private userService = inject(UserService);
    private businessUnitService = inject(BusinessUnitService);
    private contactService = inject(ContactService);
    private fiContactsService = inject(FiContactsService);
    private userSharedService = inject(FiUserSharedService);
    protected isLatestActivityColumnHidden: Signal<boolean>;

    ngOnInit(): void {
        this.isLatestActivityColumnHidden = this.userSharedService.getLatestActivityColumnHiddenSignal(this.items);
    }

    protected getUserPhoneNumber(user: IContactModel): string {
        return `${this.fiContactsService.getFullContactValue(user)} (${this.contactService.getPhoneTypeName(user.ContactType)})`;
    }

    protected getUserRolesAnchors(user: IUserListItemViewModel): INameIdObj[] {
        return user.Roles.map((role: ILightWeightObject) => {
            return { Name: role.Name, Id: role.Id };
        });
    }

    protected navigateToRole(roleId: number): void {
        this.stateService.go(FiStateNames.Role.View, { Id: roleId });
    }

    protected openUserViewDialog($event: MouseEvent, user: IUserListItemViewModel) {
        const userId = user.ObjectId ?? user.Id;
        $event.stopPropagation();
        this.userService.openUserViewDialog(userId);
    }

    protected openBusinessUnitViewDialog(businessUnitId: number): void {
        this.businessUnitService.openBusinessUnitViewDialog(businessUnitId);
    }

    protected getUserBusinessUnits(user: IUserListItemViewModel): INameIdObj[] {
        return user.BusinessUnits.map((businessUnit: ILightWeightObject) => {
            return { Name: businessUnit.Name, Id: businessUnit.Id };
        });
    }

    protected getUserAreasAnchors(user: IUserListItemViewModel): INameIdObj[] {
        return user.Areas.map((area: ILightWeightObject) => {
            return { Name: area.Name, Id: area.Id };
        });
    }

    protected getUserEmails(user: IUserListItemViewModel): INameIdObj[] {
        return user.EmailAddressContacts.map((emailContact) => {
            return { Name: this.fiContactsService.getFullContactValue(emailContact), Id: emailContact.Id };
        });
    }

    protected navigateToAreas(areaId: number): void {
        this.stateService.go(FiStateNames.Area.View, { Id: areaId });
    }

    protected getUserLatestActivity(user: IUserListItemViewModel): string {
        return this.userSharedService.getLatestActivity(user.LatestActivity, user.HasEditAccess);
    }

    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly getSystemObjectColorClass = getSystemObjectColorClass;
}
